import React, { useEffect, useRef, useState } from "react";
import {
  CDMQB_FONT_FAMILIES,
  CDMQB_FONT_SIZE_MAX,
  CDMQB_FONT_SIZE_MIN,
  CDMQB_SPLIT_LAYOUT_MIN_WIDTH,
  CDMQB_TEXT_ZOOM_PRESETS,
} from "../../SimulationConstants";
import "./Settingspanelcdmqb.css";

// Laid out like the main app's SettingsBar: "Appearance" and "Features"
// sections, one row per setting with the label on the left and the
// control on the right.

const clampFontSize = (value) =>
  Math.min(
    CDMQB_FONT_SIZE_MAX,
    Math.max(CDMQB_FONT_SIZE_MIN, Math.round(value)),
  );

// Tracks whether the screen is wide enough for the split layout, so the
// toggle can be disabled (with a note) on smaller screens.
const useCanSplit = () => {
  const query = `(min-width: ${CDMQB_SPLIT_LAYOUT_MIN_WIDTH}px)`;
  const getMatch = () =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(query).matches
      : true;

  const [canSplit, setCanSplit] = useState(getMatch);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mediaQueryList = window.matchMedia(query);
    const onChange = (event) => setCanSplit(event.matches);
    setCanSplit(mediaQueryList.matches);
    // Safari < 14 only has addListener/removeListener.
    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener("change", onChange);
      return () => mediaQueryList.removeEventListener("change", onChange);
    }
    mediaQueryList.addListener(onChange);
    return () => mediaQueryList.removeListener(onChange);
  }, [query]);

  return canSplit;
};

// Font size: − / number / +, like the main app. The number box keeps its
// own draft while typing (so "2" on the way to "22" isn't clamped to the
// minimum straight away); a value in range applies immediately, anything
// else is clamped when the box loses focus or Enter is pressed.
function FontSizeStepper({ value, onChange }) {
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const number = Number(draft);
    if (draft.trim() === "" || !Number.isFinite(number)) {
      setDraft(String(value));
      return;
    }
    const clamped = clampFontSize(number);
    setDraft(String(clamped));
    if (clamped !== value) onChange(clamped);
  };

  return (
    <div className="settingsPanelStepperCDMQB">
      <button
        type="button"
        className="settingsPanelFontButtonCDMQB"
        onClick={() => onChange(clampFontSize(value - 1))}
        disabled={value <= CDMQB_FONT_SIZE_MIN}
        aria-label="Decrease font size"
      >
        −
      </button>
      <input
        id="settingsFontSizeCDMQB"
        type="number"
        className="settingsPanelFontInputCDMQB"
        min={CDMQB_FONT_SIZE_MIN}
        max={CDMQB_FONT_SIZE_MAX}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          const number = Number(e.target.value);
          if (
            e.target.value !== "" &&
            Number.isInteger(number) &&
            number >= CDMQB_FONT_SIZE_MIN &&
            number <= CDMQB_FONT_SIZE_MAX
          ) {
            onChange(number);
          }
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
        }}
      />
      <button
        type="button"
        className="settingsPanelFontButtonCDMQB"
        onClick={() => onChange(clampFontSize(value + 1))}
        disabled={value >= CDMQB_FONT_SIZE_MAX}
        aria-label="Increase font size"
      >
        +
      </button>
    </div>
  );
}

// On/off switch (a checkbox styled as a toggle).
function Toggle({ id, checked, disabled, onChange }) {
  return (
    <input
      id={id}
      type="checkbox"
      role="switch"
      className="settingsPanelSwitchCDMQB"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
    />
  );
}

export default function SettingsPanelCDMQB(props) {
  const {
    displaySettings,
    closeSettings,
    settings,
    updateSetting,
    resetSettings,
    // Simulation skin (same setting as Account Settings > Simulation Skin).
    simulationSkin,
    simulationSkins,
    changeSimulationSkin,
  } = props;

  const canSplit = useCanSplit();
  const closeButtonRef = useRef(null);

  const selectedFont = CDMQB_FONT_FAMILIES.find(
    (font) => font.id === settings.fontFamily,
  );

  // Escape closes the panel; focus moves into it when it opens.
  useEffect(() => {
    if (!displaySettings) return undefined;

    closeButtonRef.current?.focus();

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        closeSettings();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [displaySettings, closeSettings]);

  return (
    <div
      className={`settingsPanelRootCDMQB${
        displaySettings ? " settingsPanelOpenCDMQB" : ""
      }`}
      aria-hidden={!displaySettings}
    >
      <div className="settingsPanelBackdropCDMQB" onClick={closeSettings} />

      <aside
        className="settingsPanelCDMQB"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settingsPanelTitleCDMQB"
      >
        <div className="settingsPanelHeaderCDMQB">
          <h2 id="settingsPanelTitleCDMQB" className="settingsPanelTitleCDMQB">
            Settings
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            className="settingsPanelCloseButtonCDMQB"
            onClick={closeSettings}
            aria-label="Close settings"
          >
            ×
          </button>
        </div>

        <div className="settingsPanelBodyCDMQB">
          {/* ======================== Appearance ======================== */}
          <section className="settingsPanelSectionCDMQB">
            <h3 className="settingsPanelSectionTitleCDMQB">Appearance</h3>
            <div className="settingsPanelSectionBodyCDMQB">
              <div className="settingsPanelRowCDMQB">
                <label htmlFor="settingsSimulationSkinCDMQB">
                  Simulation Skin
                </label>
                <select
                  id="settingsSimulationSkinCDMQB"
                  className="settingsPanelSelectCDMQB"
                  value={simulationSkin}
                  onChange={(e) => changeSimulationSkin(e.target.value)}
                  disabled={!changeSimulationSkin}
                >
                  {(simulationSkins || []).map((skin) => (
                    <option key={skin.id} value={skin.id}>
                      {skin.skinTitle}
                    </option>
                  ))}
                </select>
              </div>

              <div
                className={`settingsPanelRowCDMQB${
                  canSplit ? "" : " settingsPanelRowDisabledCDMQB"
                }`}
              >
                <label htmlFor="settingsSplitLayoutCDMQB">
                  Split Screen
                  {!canSplit && (
                    <span className="settingsPanelHintCDMQB">
                      Needs a screen {CDMQB_SPLIT_LAYOUT_MIN_WIDTH}px or wider
                    </span>
                  )}
                </label>
                <Toggle
                  id="settingsSplitLayoutCDMQB"
                  checked={settings.splitLayout}
                  disabled={!canSplit}
                  onChange={(checked) => updateSetting("splitLayout", checked)}
                />
              </div>

              <div className="settingsPanelRowCDMQB">
                <label htmlFor="settingsFontFamilyCDMQB">Choose Font</label>
                <select
                  id="settingsFontFamilyCDMQB"
                  className="settingsPanelSelectCDMQB"
                  value={settings.fontFamily}
                  onChange={(e) => updateSetting("fontFamily", e.target.value)}
                  // The dropdown itself shows the selected font.
                  style={
                    selectedFont && selectedFont.stack
                      ? { fontFamily: selectedFont.stack }
                      : undefined
                  }
                >
                  {CDMQB_FONT_FAMILIES.map((font) => (
                    <option
                      key={font.id}
                      value={font.id}
                      style={
                        font.stack ? { fontFamily: font.stack } : undefined
                      }
                    >
                      {font.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="settingsPanelRowCDMQB">
                <label htmlFor="settingsFontSizeCDMQB">Font Size</label>
                <FontSizeStepper
                  value={settings.fontSize}
                  onChange={(size) => updateSetting("fontSize", size)}
                />
              </div>

              <div className="settingsPanelRowCDMQB">
                <span id="settingsTextZoomLabelCDMQB">Text Zoom</span>
                <div
                  className="settingsPanelButtonGroupCDMQB"
                  role="group"
                  aria-labelledby="settingsTextZoomLabelCDMQB"
                >
                  {CDMQB_TEXT_ZOOM_PRESETS.map(
                    ({ size, previewSize, label }) => {
                      const isSelected = settings.fontSize === size;
                      return (
                        <button
                          key={size}
                          type="button"
                          className={`settingsPanelFontButtonCDMQB${
                            isSelected
                              ? " settingsPanelFontButtonSelectedCDMQB"
                              : ""
                          }`}
                          style={{ fontSize: `${previewSize}px` }}
                          onClick={() => updateSetting("fontSize", size)}
                          aria-pressed={isSelected}
                          aria-label={`${label} (${size}px)`}
                        >
                          A
                        </button>
                      );
                    },
                  )}
                </div>
              </div>

              <div className="settingsPanelRowCDMQB">
                <label htmlFor="settingsFontFamilyCDMQB">Original lab panel & instruction placement</label>
                <Toggle
                  id="settingsSplitLayoutCDMQB"
                  checked={settings.originalLabInstruction}
                  onChange={(checked) => updateSetting("originalLabInstruction", checked)}
                />
              </div>
            </div>
          </section>

          {/* ========================= Features ========================= */}
          <section className="settingsPanelSectionCDMQB">
            <h3 className="settingsPanelSectionTitleCDMQB">Features</h3>
            <div className="settingsPanelSectionBodyCDMQB">
              <div className="settingsPanelRowCDMQB">
                <label htmlFor="settingsConfirmNextCDMQB">
                  Confirm Before Next
                  <span className="settingsPanelHintCDMQB">
                    When off, Next ends the part without asking
                  </span>
                </label>
                <Toggle
                  id="settingsConfirmNextCDMQB"
                  checked={settings.confirmNextPart}
                  onChange={(checked) =>
                    updateSetting("confirmNextPart", checked)
                  }
                />
              </div>

              <div className="settingsPanelRowCDMQB">
                <span>Reset To Defaults</span>
                <button
                  type="button"
                  className="settingsPanelItemButtonCDMQB"
                  onClick={resetSettings}
                >
                  Reset
                </button>
              </div>
            </div>
          </section>
        </div>
      </aside>
    </div>
  );
}
